import {
  Button,
  Box,
  Icon,
  IconButton,
  Stack,
  TextField,
  Typography,
  CircularProgress,
  Form,
} from '@ace-clublabs/shared-component-library';
import React, { useEffect, useReducer, useState, useContext } from 'react';
import { registrationStatusReducer } from '../../../application/reducers/registrationDetailsReducers';
import { generateRegistrationStatus, RegistrationStatus } from '../../../application/viewmodels/RegistrationFormStatus';
import { validateEmail, validatePassword, validateConfirmPassword } from '../../../utils/validations';
import { MembershipAppStateContext } from '../../MembershipAppState/MembershipAppState';
import { Pages } from '@lib/types';
import { useHistory } from 'react-router-dom';
import { UtagHelper } from '../../../utils/UtagHelper';
import { FlowType } from '@ace-clublabs/common-membership-api-types';

interface IRegisterAccountProps {
  defaultEmail?: string;
  sessionId: string;
  tags?: Record<string, string>;
  onRegistrationComplete?: () => void;
  flow?: FlowType;
}

export const StatelessRegisterAccount = ({
  defaultEmail,
  sessionId,
  tags,
  onRegistrationComplete,
  flow,
}: IRegisterAccountProps) => {
  const [registrationForm, dispatchRegistrationFormUpdate] = useReducer<
    React.Reducer<
      RegistrationStatus,
      { field: string; value: string | null; data?: { password?: string; email?: string } }
    >
  >(registrationStatusReducer, generateRegistrationStatus());
  const [enterNewEmail, setEnterNewEmail] = useState(false);
  const [displayErrorMessage, setDisplayErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [clickedEmailEditButton, setEmailEditButtonClicked] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasRegistrationError, setHasRegistrationError] = useState(false);

  const { registerIdentityAccount } = useContext(MembershipAppStateContext);
  const handleEmailEditClick = () => {
    setEnterNewEmail(true);
    setEmailEditButtonClicked(true);
  };

  const history = useHistory();
  const hasRegistrationValidationErrors = () => {
    const emailErr = validateEmail(registrationForm.email.value ?? '');
    const passwordErr = validatePassword(registrationForm.password.value ?? '', registrationForm.email.value ?? '');
    const confirmErr = validateConfirmPassword(
      registrationForm.confirmPassword.value ?? '',
      registrationForm.password.value ?? ''
    );
    return emailErr !== null || passwordErr !== null || confirmErr !== null;
  };

  const handleRegister = async () => {
    if (hasRegistrationValidationErrors()) {
      dispatchRegistrationFormUpdate({ field: 'registrationForm', value: null });
      return;
    }

    setIsLoading(true);
    try {
      const response = await registerIdentityAccount(
        sessionId,
        registrationForm.email.value!,
        registrationForm.password.value!
      );

      if (response?.success) {
        setEnterNewEmail(false);
        setDisplayErrorMessage(false);
        onRegistrationComplete?.();
        setIsLoading(false);
      } else {
        const emailInUseError = response?.error_code === 'email_in_use';
        const message = emailInUseError
          ? 'Please enter a different email address. This email address will be linked to your new membership.'
          : 'Unable to register account. Please try again.';
        setEnterNewEmail(true);
        setDisplayErrorMessage(true);
        setHasRegistrationError(true);
        setErrorMessage(message);
        setIsLoading(false);
        UtagHelper.link({
          ...tags,
          error: `membership:join:membership registration-${emailInUseError ? 'email in use' : 'system error'}`,
          page: 'membership:join:confirmation-membership registration error',
        });
      }
    } catch {
      setIsLoading(false);
      history.push({
        pathname: Pages.SorryPage,
        state: { referer: Pages.StatelessConfirmationPage, flow },
      });
    }
  };

  useEffect(() => {
    if (defaultEmail) {
      dispatchRegistrationFormUpdate({
        field: 'email',
        value: defaultEmail,
      });
    }
  }, [defaultEmail]);

  return (
    <Box className="register-account-container">
      <Box data-di-mask="true" className="register-inputs-list qm-mask">
        <Form onSubmit={handleRegister}>
          <Typography variant="b1Medium" className="register-account-title">
            Finish creating your account
          </Typography>
          {displayErrorMessage ? (
            <Typography variant="b3Regular" className="enter-new-email" sx={{ color: 'red' }}>
              {errorMessage}
            </Typography>
          ) : null}
          {enterNewEmail ? (
            <TextField
              label={
                <Stack direction="row" alignItems="center">
                  <Typography variant="b1Regular" id="default-email-address" className="default-email-address">
                    Email
                  </Typography>
                  <Typography variant="b3Regular" className="email-username-disclaimer">
                    {' '}
                    — this will be your username
                  </Typography>
                </Stack>
              }
              validationErrorMessage={registrationForm.email.errorMessage ?? undefined}
              type="text"
              error={registrationForm.email.error}
              showClearButton={true}
              onChange={(v) => {
                if (hasRegistrationError) setHasRegistrationError(false);
                dispatchRegistrationFormUpdate({
                  field: 'email',
                  value: v.currentTarget.value,
                });
              }}
              onClearButtonClick={() => {
                dispatchRegistrationFormUpdate({
                  field: 'email',
                  value: '',
                });
              }}
              defaultValue={clickedEmailEditButton ? registrationForm.email.value : ''}
              id="emailAddress"
              fullWidth
              sx={{
                marginTop: '1rem',
                '& #emailAddress.Mui-disabled': {
                  WebkitTextFillColor: '#707070',
                  color: '#707070',
                  backgroundColor: '#FFFFFF',
                },
                '& .MuiInputLabel-root.Mui-disabled': {
                  color: '#404040',
                },
                '& .Mui-disabled .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#D0D0D0',
                },
                '& .MuiInputBase-input': {
                  ':hover': {
                    backgroundColor: '#FFFFFF !important',
                  },
                },
              }}
            />
          ) : (
            <Stack direction="column" justifyContent="space-between" className="email-text-display" marginTop="1rem">
              <Stack direction="row" alignItems="center">
                <Typography variant="b1Regular" id="default-email-address" className="default-email-address">
                  Email
                </Typography>
                <Typography variant="b3Regular" className="email-username-disclaimer">
                  {' '}
                  — this will be your username
                </Typography>
              </Stack>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography variant="b1Medium" className="default-email-value" marginTop="0.5rem">
                  {registrationForm.email.value ?? ''}
                </Typography>
                <IconButton
                  aria-label="edit email"
                  onClick={handleEmailEditClick}
                  id="edit-email-button"
                  sx={{ fontSize: '1rems' }}
                >
                  <Icon iconName="pen-regular" />
                </IconButton>
              </Stack>
            </Stack>
          )}

          <TextField
            value={registrationForm.password.value}
            label={<Typography variant="b1Regular">Create password</Typography>}
            id="createPassword"
            type="password"
            fullWidth
            required
            error={registrationForm.password.error}
            validationErrorMessage={registrationForm.password.errorMessage}
            onChange={(v) => {
              if (hasRegistrationError) setHasRegistrationError(false);
              dispatchRegistrationFormUpdate({
                field: 'password',
                value: v.currentTarget.value,
                data: { email: registrationForm.email.value },
              });
              if (registrationForm.confirmPassword.value) {
                dispatchRegistrationFormUpdate({
                  field: 'confirmPassword',
                  value: registrationForm.confirmPassword.value,
                  data: { password: v.currentTarget.value },
                });
              }
            }}
            sx={{
              marginTop: '1rem',
              '& .MuiInputBase-input': {
                ':hover': {
                  backgroundColor: '#FFFFFF !important',
                },
              },
            }}
          />
          <Box className="password-requirement-section">
            <Typography className="password-requirement-title" variant="b1Medium">
              Password requirements:
            </Typography>
            <ul>
              <li>Case sensitive</li>
              <li>Be 8-31 characters</li>
              <li>Include at least 1 number and 1 letter</li>
              <li>Can include: {'^ - ! @ # { } ~ $ _ .'} </li>
              <li>Cannot contain the word "password"</li>
              <li>Cannot be your email</li>
            </ul>
          </Box>
          <TextField
            value={registrationForm.confirmPassword.value}
            label={<Typography variant="b1Regular">Confirm password</Typography>}
            id="confirmPassword"
            type="password"
            fullWidth
            required
            error={registrationForm.confirmPassword.error}
            validationErrorMessage={registrationForm.confirmPassword.errorMessage}
            onChange={(v) => {
              if (hasRegistrationError) setHasRegistrationError(false);
              dispatchRegistrationFormUpdate({
                field: 'confirmPassword',
                value: v.currentTarget.value,
                data: { password: registrationForm.password.value ?? '' },
              });
            }}
            sx={{
              marginTop: '1rem',
              '& .MuiInputBase-input': {
                ':hover': {
                  backgroundColor: '#FFFFFF !important',
                },
              },
            }}
          />
          <Button
            label={isLoading ? 'Registering...' : 'Complete registration'}
            fullWidth
            type="submit"
            variant="contained"
            size="small"
            id="registerButton"
            disabled={isLoading || hasRegistrationValidationErrors() || hasRegistrationError}
            startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : undefined}
          ></Button>
        </Form>
      </Box>
    </Box>
  );
};
